// src/components/borrowing/BorrowerResponseModal.jsx
import React, { useState } from 'react';
import { 
  X, AlertCircle, Handshake, CheckSquare, Upload, FileText, 
  Loader2, MessageSquare, Shield, DollarSign
} from 'lucide-react';
import { reportService } from '../../services/reportService';
import { fileStorageService } from '../../services/fileStorageService';
import { BorrowerResponseType, ReportStatus } from '../constants';

const BorrowerResponseModal = ({ isOpen, onClose, report, onResponseSubmitted }) => {
  const [responseType, setResponseType] = useState(null);
  const [responseText, setResponseText] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  if (!isOpen || !report) return null;

  const responseTypes = [
    {
      id: BorrowerResponseType.DISPUTE,
      label: 'Dispute This Claim',
      description: 'I believe this claim is inaccurate or fraudulent',
      icon: AlertCircle,
      color: 'orange',
      placeholder: 'Explain why you are disputing this claim. Include any relevant details about the situation, incorrect information, or why this debt is not valid...'
    },
    {
      id: BorrowerResponseType.SETTLEMENT_OFFER,
      label: 'Offer Settlement',
      description: 'I want to negotiate a payment arrangement',
      icon: Handshake,
      color: 'blue',
      placeholder: 'Describe your proposed settlement terms. Include the amount you can pay, proposed payment schedule, or any other arrangement you would like to discuss...'
    },
    {
      id: BorrowerResponseType.CLAIM_PAID,
      label: 'Already Paid',
      description: 'I have already paid this debt',
      icon: CheckSquare,
      color: 'emerald',
      placeholder: 'Provide details of your payment including date, method (cash, transfer, etc.), and any reference numbers. Attach proof of payment if available...'
    },
  ];

  const selectedType = responseTypes.find(t => t.id === responseType);

  const handleFileChange = async (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      
      if (file.size > 5 * 1024 * 1024) {
        setError('File size exceeds 5MB limit.');
        return;
      }

      setUploadingFiles(true);
      setError(null);
      
      try {
        const previewData = await fileStorageService.fileToBase64(file);
        const tempItem = {
          id: Math.random().toString(36).substr(2, 9),
          name: file.name,
          type: file.type,
          data: previewData,
          file: file,
          isTemp: true
        };
        setAttachments([...attachments, tempItem]);
      } catch (err) {
        console.error('Error processing file:', err);
        setError('Failed to process file. Please try again.');
      } finally {
        setUploadingFiles(false);
      }
    }
  };

  const removeAttachment = (id) => {
    setAttachments(attachments.filter(a => a.id !== id));
  };

  const handleSubmit = async () => {
    if (!responseType || !responseText.trim()) {
      setError('Please select a response type and provide details.');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      // Upload evidence files
      const uploadedFiles = [];
      for (const attachment of attachments) {
        if (attachment.isTemp && attachment.file) {
          const result = await fileStorageService.uploadBorrowerEvidence(report.id, attachment.file);
          uploadedFiles.push({
            id: attachment.id,
            name: attachment.name,
            type: attachment.type,
            url: result.url,
            path: result.path
          });
        }
      }

      // Submit response
      await reportService.addBorrowerResponse(report.id, {
        text: responseText,
        type: responseType,
        attachments: uploadedFiles
      });

      onResponseSubmitted?.();
      onClose();
    } catch (err) {
      console.error('Error submitting response:', err);
      setError('Failed to submit response. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const getColorClasses = (color, isSelected) => {
    const colors = {
      orange: {
        bg: isSelected ? 'bg-orange-900/30' : 'bg-slate-900',
        border: isSelected ? 'border-orange-500 ring-1 ring-orange-500' : 'border-slate-700',
        text: isSelected ? 'text-orange-400' : 'text-slate-400',
        icon: 'text-orange-500'
      },
      blue: {
        bg: isSelected ? 'bg-blue-900/30' : 'bg-slate-900',
        border: isSelected ? 'border-blue-500 ring-1 ring-blue-500' : 'border-slate-700',
        text: isSelected ? 'text-blue-400' : 'text-slate-400',
        icon: 'text-blue-500'
      },
      emerald: {
        bg: isSelected ? 'bg-emerald-900/30' : 'bg-slate-900',
        border: isSelected ? 'border-emerald-500 ring-1 ring-emerald-500' : 'border-slate-700',
        text: isSelected ? 'text-emerald-400' : 'text-slate-400',
        icon: 'text-emerald-500'
      }
    };
    return colors[color] || colors.orange;
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-in fade-in">
      <div className="bg-slate-800 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden border border-slate-700 shadow-2xl animate-in slide-in-from-bottom-4">
        
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-slate-700 bg-slate-900">
          <div className="flex items-center gap-3">
            <div className="bg-blue-500/20 p-2 rounded-lg">
              <MessageSquare className="text-blue-400" size={20} />
            </div>
            <div>
              <h2 className="text-lg font-bold text-white">Respond to Claim</h2>
              <p className="text-xs text-slate-400">
                Claim by {report.lenderName} • ${report.amount?.toLocaleString()}
              </p>
            </div>
          </div>
          <button 
            onClick={onClose}
            className="text-slate-400 hover:text-white p-2 hover:bg-slate-700 rounded-lg transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
          
          {/* Claim Summary */}
          <div className="bg-slate-900/50 rounded-xl p-4 border border-slate-700/50 mb-6">
            <div className="flex justify-between items-start mb-3">
              <div>
                <p className="text-xs text-slate-500 uppercase font-bold">Claim Against You</p>
                <p className="text-white font-bold mt-1">{report.borrowerName}</p>
              </div>
              <div className="text-right">
                <p className="text-xs text-slate-500 uppercase font-bold">Amount</p>
                <p className="text-red-400 font-bold font-mono text-lg">${report.amount?.toLocaleString()}</p>
              </div>
            </div>
            <div className="bg-slate-800 rounded-lg p-3 border border-slate-700/50">
              <p className="text-sm text-slate-300 italic">"{report.description}"</p>
            </div>
            <div className="flex gap-4 mt-3 text-xs text-slate-500">
              <span>Due: {report.dueDate}</span>
              <span>•</span>
              <span>Agreement: {report.agreementDate}</span>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-4 p-3 bg-red-500/20 border border-red-500/50 rounded-lg text-red-400 text-sm flex items-center gap-2 animate-in slide-in-from-top-2">
              <AlertCircle size={16} />
              {error}
            </div>
          )}

          {/* Response Type Selection */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-slate-300 mb-3">
              How would you like to respond?
            </label>
            <div className="space-y-3">
              {responseTypes.map((type) => {
                const Icon = type.icon;
                const isSelected = responseType === type.id;
                const colors = getColorClasses(type.color, isSelected);
                
                return (
                  <button
                    key={type.id}
                    type="button"
                    onClick={() => setResponseType(type.id)}
                    className={`w-full p-4 rounded-xl border text-left transition-all ${colors.bg} ${colors.border} hover:border-slate-600`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`p-2 rounded-lg ${isSelected ? 'bg-white/10' : 'bg-slate-800'}`}>
                        <Icon size={20} className={colors.icon} />
                      </div>
                      <div className="flex-1">
                        <p className={`font-bold ${isSelected ? 'text-white' : 'text-slate-300'}`}>
                          {type.label}
                        </p>
                        <p className={`text-sm mt-0.5 ${colors.text}`}>
                          {type.description}
                        </p>
                      </div>
                      {isSelected && (
                        <div className={`w-5 h-5 rounded-full ${type.color === 'orange' ? 'bg-orange-500' : type.color === 'blue' ? 'bg-blue-500' : 'bg-emerald-500'} flex items-center justify-center`}>
                          <CheckSquare size={12} className="text-white" />
                        </div>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Response Text */}
          {responseType && (
            <div className="mb-6 animate-in slide-in-from-top-2">
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Your Response
              </label>
              <textarea
                value={responseText}
                onChange={(e) => setResponseText(e.target.value)}
                rows={5}
                className="w-full bg-slate-900 border border-slate-700 rounded-lg p-4 text-white text-sm focus:ring-2 focus:ring-blue-500 outline-none placeholder-slate-500 resize-none transition-all"
                placeholder={selectedType?.placeholder}
              />
              <p className="text-xs text-slate-500 mt-2">
                Your response will be publicly visible alongside the original claim.
              </p>
            </div>
          )}

          {/* Evidence Upload */}
          {responseType && (
            <div className="mb-6 animate-in slide-in-from-top-2">
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Supporting Evidence (Recommended)
              </label>
              <div className="border-2 border-dashed border-slate-700 rounded-lg p-4 hover:bg-slate-700/30 transition-colors">
                <input
                  type="file"
                  id="evidence-upload"
                  className="hidden"
                  accept="image/*,.pdf"
                  onChange={handleFileChange}
                  disabled={uploadingFiles}
                />
                <label
                  htmlFor="evidence-upload"
                  className={`cursor-pointer flex flex-col items-center justify-center gap-2 ${
                    uploadingFiles ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                >
                  {uploadingFiles ? (
                    <>
                      <Loader2 className="text-blue-500 animate-spin" size={24} />
                      <span className="text-sm text-slate-400">Uploading...</span>
                    </>
                  ) : (
                    <>
                      <Upload className="text-blue-500" size={24} />
                      <span className="text-sm text-slate-400 font-medium">
                        Upload proof of payment, receipts, or other evidence
                      </span>
                      <span className="text-xs text-slate-600">Max 5MB • Images or PDF</span>
                    </>
                  )}
                </label>
              </div>

              {attachments.length > 0 && (
                <div className="grid grid-cols-4 gap-2 mt-3">
                  {attachments.map((file) => (
                    <div key={file.id} className="relative group bg-slate-900 rounded-lg border border-slate-700 p-1">
                      <button
                        type="button"
                        onClick={() => removeAttachment(file.id)}
                        className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full p-0.5 shadow hover:bg-red-600 transition-colors z-10"
                      >
                        <X size={10} />
                      </button>
                      <div className="h-16 w-full rounded overflow-hidden flex items-center justify-center bg-black/50">
                        {file.type.startsWith('image/') ? (
                          <img src={file.data} alt={file.name} className="h-full w-full object-cover" />
                        ) : (
                          <FileText className="text-red-400" size={24} />
                        )}
                      </div>
                      <p className="text-[10px] text-slate-500 mt-1 truncate px-1">{file.name}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Info Box */}
          <div className="bg-blue-900/20 border border-blue-500/30 p-4 rounded-lg mb-6">
            <div className="flex items-start gap-3">
              <Shield className="text-blue-400 mt-0.5" size={18} />
              <div>
                <p className="text-blue-400 font-bold text-sm">Your Rights</p>
                <ul className="text-xs text-slate-400 mt-1 space-y-1">
                  <li>• Your response will be permanently attached to this report</li>
                  <li>• The lender will be notified and can reply to your response</li>
                  <li>• If you claim payment, provide proof for faster resolution</li>
                  <li>• False claims may result in account penalties</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-slate-700 bg-slate-900/50">
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 py-3 px-4 rounded-lg bg-slate-700 text-slate-300 font-bold hover:bg-slate-600 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={!responseType || !responseText.trim() || submitting || uploadingFiles}
              className="flex-1 py-3 px-4 rounded-lg bg-blue-600 text-white font-bold hover:bg-blue-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {submitting ? (
                <>
                  <Loader2 size={18} className="animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <MessageSquare size={18} />
                  Submit Response
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BorrowerResponseModal;